import { api } from '@/lib/axios'
import type { NewsSummary, SummarizeRequest } from '../types/ai.types'
import { firebaseAuthService } from '@/features/auth/api/authService'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AI API Client
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const aiApi = {
  /**
   * Generate AI summary for articles
   * @param request - Summarize request with keyword and articles
   * @returns AI-generated news summary with insights
   */
  async summarizeArticles(request: SummarizeRequest): Promise<NewsSummary> {
    const { data } = await api.post<NewsSummary>('/api/v1/ai/summarize', request)
    return data
  },

  /**
   * Stream AI summary for articles using Server-Sent Events (SSE)
   * Uses fetch API with ReadableStream for POST support
   * @param request - Summarize request with keyword and articles
   * @param onChunk - Callback for each text chunk received
   * @param onComplete - Callback when streaming is complete
   * @param onError - Callback for errors
   * @returns Abort controller for cancellation
   */
  async streamSummarizeArticles(
    request: SummarizeRequest,
    onChunk: (chunk: string) => void,
    onComplete: () => void,
    onError: (error: Error) => void
  ): Promise<AbortController> {
    const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
    const controller = new AbortController()

    try {
      // Get Firebase ID token for authentication
      const idToken = await firebaseAuthService.getIdToken()
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }

      // Add Authorization header if token is available
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`
      }

      const response = await fetch(`${baseURL}/api/v1/ai/summarize/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal: controller.signal,
      })

      if (!response.ok) {
        // The credit check runs before the stream opens, so exhaustion arrives as a
        // plain 429 carrying `{code, message}` at the top level (#114) — "오늘 AI 요약
        // 10회를 모두 사용했어요." Discarding that body left an English status line
        // that nothing rendered, so hitting the cap looked like a broken app. This
        // path is fetch, not axios, so no interceptor is going to read it for us.
        const body = (await response.json().catch(() => null)) as { message?: string } | null
        throw new Error(body?.message || `HTTP error! status: ${response.status}`)
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()

        if (done) {
          onComplete()
          break
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true })

        // Process complete SSE messages
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonData = JSON.parse(line.slice(6))
              if (jsonData.content) {
                onChunk(jsonData.content)
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e)
            }
          } else if (line.startsWith('event: done')) {
            onComplete()
            return controller
          } else if (line.startsWith('event: error')) {
            const errorLine = lines[lines.indexOf(line) + 1]
            if (errorLine?.startsWith('data: ')) {
              try {
                const errorData = JSON.parse(errorLine.slice(6))
                onError(new Error(errorData.error || 'Unknown error'))
              } catch (e) {
                onError(new Error('Streaming error'))
              }
            }
            return controller
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Stream was cancelled, this is expected
        return controller
      }
      onError(error instanceof Error ? error : new Error('Streaming failed'))
    }

    return controller
  },
}
